/**
 * useEscapeToClose
 *
 * Closes a dialog / modal / slide-out on Escape key in desktop mode only.
 * Tablet/mobile (viewport < 768 px — the Tailwind `md` breakpoint used
 * elsewhere in the app) intentionally do nothing, because:
 *   • on-screen keyboards don't reliably surface an Escape key
 *   • staff on the tablet view dismiss modals by tapping the explicit
 *     close button / backdrop
 *
 * Nesting
 * ───────
 * A module-level LIFO stack stores onClose callbacks for every modal that
 * is currently mounted. Escape always invokes the top-most callback and
 * stops there, so opening a confirm dialog inside an edit modal inside a
 * detail modal closes them one at a time — same behaviour as native OS
 * dialogs.
 *
 * Usage
 * ─────
 *     import useEscapeToClose from './useEscapeToClose';
 *
 *     const MyModal = ({ onClose, ... }) => {
 *         useEscapeToClose(true, onClose);          // unconditional
 *         // or
 *         useEscapeToClose(isOpen, onClose);        // gated on open flag
 *         ...
 *     };
 *
 * The hook is a no-op when `active` is falsy, so callers can leave it
 * mounted at the top of a component and let it follow the open prop.
 */

import { useEffect } from 'react';

// LIFO stack of currently-active onClose callbacks. Newest at the end.
const stack = [];

// Single window-level keydown listener — installed lazily on first push,
// torn down when the stack drains. Keeps the global listener count to 1
// regardless of how many modals are mounted.
let listenerInstalled = false;

const isDesktop = () => {
    if (typeof window === 'undefined' || !window.matchMedia) return true;
    return window.matchMedia('(min-width: 768px)').matches;
};

const onKeyDown = (e) => {
    if (e.key !== 'Escape') return;
    if (!isDesktop()) return;
    if (stack.length === 0) return;
    const top = stack[stack.length - 1];
    // Stop here so only the top-most modal sees the keystroke.
    e.stopPropagation();
    top();
};

const installListener = () => {
    if (listenerInstalled) return;
    window.addEventListener('keydown', onKeyDown);
    listenerInstalled = true;
};

const uninstallListener = () => {
    if (!listenerInstalled) return;
    window.removeEventListener('keydown', onKeyDown);
    listenerInstalled = false;
};

const useEscapeToClose = (active, onClose) => {
    useEffect(() => {
        if (!active || typeof onClose !== 'function') return undefined;

        // Push this modal's close handler onto the stack and ensure the
        // global keydown listener is wired up.
        stack.push(onClose);
        installListener();

        return () => {
            // On unmount (or when `active` flips false / `onClose` changes),
            // remove this entry. We search from the top because the most
            // common case is the top entry unmounting first.
            for (let i = stack.length - 1; i >= 0; i--) {
                if (stack[i] === onClose) {
                    stack.splice(i, 1);
                    break;
                }
            }
            if (stack.length === 0) uninstallListener();
        };
    }, [active, onClose]);
};

export default useEscapeToClose;